import { Modal } from 'antd';
import { RcFile } from 'antd/es/upload';
import prepareDocuments from 'helpers/prepareDocuments';
import { TFunction } from 'i18next';
import { IDocument } from 'interfaces';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { setIsVisibleUploadModal } from 'store/slices/windowStateSlice';
import classes from './Postfix.module.scss';
import UploadingComponent from 'components/UploadingComponent/UploadingComponent';
import api from 'api';
import { handleDeleteChoosenObjects, handleUploadFiles } from 'store/reducers/documentsCreator';
import { useNavigate, useParams } from 'react-router';
import { getTheme } from 'helpers/getTheme';

type Props = {
  isOpen: boolean;
  duplicateDocuments: {
    forUpload: RcFile[];
    forDelete: IDocument[];
    generatedNames: string[];
  };
  onCancel: () => void;
  t: TFunction;
};

export default function PostfixModal({
  isOpen,
  duplicateDocuments,
  t,
  onCancel,
}: Props) {
  const dispatch = useAppDispatch();
  const { isUploadingDocuments } = useAppSelector((state) => state.windowState);

  const navigate = useNavigate();
  const isHaveVersions = duplicateDocuments.forDelete.some(({ prev_version }) => prev_version);
  const currentFolderId = useParams().folderId || null;
  
  let finalDocuments: IDocument[] = [];

  if (isHaveVersions) {
    const versionsStackDocuments: Record<string, IDocument[]> = {};
    duplicateDocuments.forDelete.forEach((docs) => {
      docs.prev_version || !versionsStackDocuments[docs.name]
        ? (versionsStackDocuments[docs.name] = [docs])
        : versionsStackDocuments[docs.name].push(docs);
    });

    for (const key in versionsStackDocuments) {
      finalDocuments = [...finalDocuments, ...versionsStackDocuments[key]];
    }
  }

  const onUploadClick = async () => {
    onCancel();
    dispatch(setIsVisibleUploadModal(false));
    const filesWithNewNames = duplicateDocuments.forUpload.map((file, idx) => {
      if (duplicateDocuments.generatedNames[idx]) {
        const newFile = new File([file], duplicateDocuments.generatedNames[idx], {
          type: file.type,
        }) as RcFile;
        Object.defineProperty(newFile, 'webkitRelativePath', {
          value: file.webkitRelativePath,
        });
        return newFile
      }
      return file;
    });

    dispatch(handleUploadFiles({otherFileList: filesWithNewNames, t, navigate}));
    // handleUpload(filesWithNewNames);
  };

  const onReplaceClick = async () => {
    onCancel();
    dispatch(setIsVisibleUploadModal(false));

    if (isHaveVersions) {
      await Promise.all(
        finalDocuments.map(async (docs) => {
          const forUpload = duplicateDocuments.forUpload.find(
            ({ name }) => name === docs.name
          )!;
          const preparedDocumentKeys = prepareDocuments([docs], []).map(
            ({ key }) => ({ key })
          ) as IDocument[];

          await dispatch(handleDeleteChoosenObjects({
            forDeleteDocuments: preparedDocumentKeys,
            currentFolderId,
            documentList: duplicateDocuments.forDelete,
            include_versions: false,
            t,
          }));

          if (docs.prev_version) {
            const formData = new FormData();
            formData.append(
              'document',
              new File([forUpload], forUpload.name, { type: forUpload.type })
            );
            formData.append('parent_version_id', docs.prev_version);
            await api.uploadDocumentVersion({ formData: formData });
          } else {
            await dispatch(handleUploadFiles({otherFileList: [forUpload], t, navigate}));
          }
        })
      );
    } else {
      const preparedDocumentKeys = prepareDocuments(
        duplicateDocuments.forDelete,
        []
      ).map(({ key }) => ({ key })) as IDocument[];

      await dispatch(handleDeleteChoosenObjects({
        forDeleteDocuments: preparedDocumentKeys,
        currentFolderId,
        documentList: duplicateDocuments.forDelete,
        include_versions: false,
        t,
      }));
      
      await dispatch(handleUploadFiles({otherFileList: duplicateDocuments.forUpload, t, navigate}));
    }
  };

  const handleCancelAndClose = (event: any) => {
    if (event.currentTarget.id === 'cancelButton') {
      onReplaceClick();
    } else {
      onCancel();
    }
  };

  return (
    <>
      <Modal
        title={t('Documents.modals.postfix.title')}
        onCancel={handleCancelAndClose}
        open={isOpen}
        cancelButtonProps={{ id: 'cancelButton' }}
        cancelText={t('Documents.modals.postfix.replaceButton')}
        okText={t('Documents.modals.postfix.uploadButton')}
        onOk={onUploadClick}
        className={getTheme()}
        classNames={{ body: classes.body, footer: classes.footer }}
      >
        <div>
          {t('Documents.modals.postfix.body')}{' '}
          {isHaveVersions
            ? finalDocuments.map(({ name }) => name).join(', ')
            : duplicateDocuments.forDelete.map(({ name }) => name).join(', ')}
        </div>
        <div style={{ marginTop: 10 }}>{t('Documents.modals.postfix.otherFiles')}</div>
        {isUploadingDocuments && <UploadingComponent />}
      </Modal>
    </>
  );
}
